import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useState } from 'react';

import {
  Level,
  useSnackNotificationContext,
} from '@sorare/core/src/contexts/snackNotification';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery.ts';

import { JoinSquadDialog } from '../JoinSquadDialog/index.tsx';
import { SquadCreateOrJoin } from '../SquadCreateOrJoin/index.tsx';
import { CODE_PREFIX, getCodeForLookup } from '../SquadJoin/index.tsx';
import { SquadJoined } from '../SquadJoined/index.tsx';
import type {
  SquadJoinFromLinkQuery,
  SquadJoinFromLinkQueryVariables,
} from './__generated__/index.graphql.ts';

const SQUAD_JOIN_FROM_LINK_QUERY = gql`
  query SquadJoinFromLinkQuery($id: ID, $joinSecret: String) {
    cardSet {
      id
      squad(id: $id, joinSecret: $joinSecret) {
        id
        ...JoinSquadDialog_squad
        ...SquadJoined_squad
      }
    }
  }
  ${JoinSquadDialog.fragments.squad}
  ${SquadJoined.fragments.squad}
` as TypedDocumentNode<SquadJoinFromLinkQuery, SquadJoinFromLinkQueryVariables>;

const toLookupVariables = (code: string) => {
  const isLegacyJoinSecret = code.toUpperCase().startsWith(CODE_PREFIX);
  if (isLegacyJoinSecret) {
    return { joinSecret: `${CODE_PREFIX}${getCodeForLookup(code, true)}` };
  }

  return { id: code };
};

type Props = {
  code: string;
};

export const SquadJoinFromLink = ({ code }: Props) => {
  const { data, loading, error } = useQuery(SQUAD_JOIN_FROM_LINK_QUERY, {
    variables: toLookupVariables(code),
  });
  const [dismissed, setDismissed] = useState(false);
  const [joined, setJoined] = useState(false);
  const { showNotification } = useSnackNotificationContext();

  const squad = data?.cardSet?.squad ?? null;
  const notFound = !loading && (!!error || !squad);
  const open = !!squad && !dismissed && !joined;

  useEffect(() => {
    if (notFound) {
      showNotification('squadNotFound', undefined, { level: Level.ERROR });
    }
  }, [notFound, showNotification]);

  if (joined && squad) {
    return <SquadJoined squad={squad} />;
  }

  return (
    <>
      <SquadCreateOrJoin />
      <JoinSquadDialog
        squad={squad}
        open={open}
        onClose={() => setDismissed(true)}
        onJoined={() => setJoined(true)}
      />
    </>
  );
};
